import { Alert, Box, Button, Typography } from "@mui/material";
import { InlineLinkButton } from './InlineLinkButton.styles';
import React from "react";
import { ImportedContactsCsvSummary } from "src/modules/contacts/types";
import { downloadInvalidContactsCsv } from "src/modules/contacts/helpers/downloadInvalidContactsCsv";

type ReviewScreenProps = {
  summary: ImportedContactsCsvSummary;
  onAddToGroups: () => void;
  onSkip: () => void;
  onBack?: () => void;
};

export default function ReviewScreen({ summary, onAddToGroups, onSkip, onBack }: ReviewScreenProps) {
  const {
    createdContacts = [],
    existedContacts = [],
    invalidContacts = [],
    repeatedContactsCount = 0,
    selectedColumns,
  } = summary;

  // A reported row carries a contact id when it was imported with the invalid fields dropped,
  // and none when it was rejected outright
  const rejectedContacts = invalidContacts.filter(contact => !contact.contactId);
  const contactsWithErrors = invalidContacts.filter(contact => !!contact.contactId);
  const hasInvalidContacts = invalidContacts.length > 0;
  const hasNewOrUpdatedContacts = createdContacts.length > 0 || existedContacts.length > 0;

  return (
    <>
    <Box sx={{ width: "100%", mx: 5 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Your contacts have been processed successfully!
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
          <Typography variant="body2">
            • <strong>{createdContacts.length}</strong> new{createdContacts.length === 1 ? " contact" : " contacts"} created
          </Typography>
          <Typography variant="body2">
            • <strong>{existedContacts.length}</strong> existing{existedContacts.length === 1 ? " contact" : " contacts"} updated
          </Typography>
          <Typography variant="body2">
            • <strong>{repeatedContactsCount}</strong> duplicate{repeatedContactsCount === 1 ? " contact" : " contacts"} found
          </Typography>
          {contactsWithErrors.length > 0 && (
            <Typography variant="body2">
              • <strong>{contactsWithErrors.length}</strong>{contactsWithErrors.length === 1 ? " contact" : " contacts"} uploaded with error(s)
            </Typography>
          )}
          {rejectedContacts.length > 0 && (
            <Typography variant="body2">
              • <strong>{rejectedContacts.length}</strong>{rejectedContacts.length === 1 ? " contact" : " contacts"} unable to be uploaded
            </Typography>
          )}
        </Box>
      </Box>

      {hasInvalidContacts && (
        <Alert severity="warning" sx={{ mb: 3, justifyContent: "center" }}>
          Some contacts need your attention.{" "}
          <InlineLinkButton
            size="small"
            onClick={() => downloadInvalidContactsCsv(invalidContacts, null, selectedColumns)}
          >
            Download
          </InlineLinkButton>{" "}
          the list of reported contacts to review and fix any issues before uploading again.
        </Alert>
      )}
    </Box>
    <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          {onBack && (
            <Button
              variant="outlined"
              onClick={onBack}
              sx={{ minWidth: "auto" }}
            >
              Back
            </Button>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button variant="outlined" color="secondary" onClick={onSkip}>
            Skip
          </Button>
          {hasNewOrUpdatedContacts ? (
            <Button variant="contained" onClick={onAddToGroups}>
              Add Contacts to Group(s)
            </Button>
          ) : (
            <Button variant="contained" onClick={onSkip}>
              Close
            </Button>
          )}
        </Box>
      </Box>
    </>
  );
}
